import { Column, FlexBox } from '@dixa/lib-shared-habla';
import type { OpeningHours } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import { useCallback, useMemo } from 'react';

import {
  ListItemType,
  useScheduleContext,
} from 'components/settings/components/sections/openingHours/views/schedule/schedule.context';
import { validateIntervals } from 'components/settings/components/sections/openingHours/views/schedule/utils/validate';
import type { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';

import { IntervalsSelectProps } from '../intervalsSelect/intervalsSelect';
import { ListHeader } from '../listHeader/listHeader';
import { ListItem } from '../listItem/listItem';
import { ListItemContext } from '../listItem/listItem.context';

const headers = [
  {
    label: 'Standard business hours',
  },
  {
    label: 'Standard audio message',
    helpText: `Customers will hear this audio message when they call you outside of your standard business hours.
      This applies to all phone flows which use this schedule.`,
  },
  {
    label: 'Standard auto-reply (optional)',
    helpText: `If set, customers will receive this auto-reply when they email you outside your standard business hours.
      This applies to all email flows which use this schedule.`,
  },
];

type Props = {
  audioMessage: AnnouncementItem;
  autoReply?: null | string;
  onAudioMessageChange: (audioMessage: AnnouncementItem) => void;
  onAutoReplyChange: (autoReply?: string) => void;
};

export const BusinessHoursList = ({
  audioMessage,
  autoReply,
  onAudioMessageChange,
  onAutoReplyChange,
}: Props): JSX.Element => {
  const {
    activeView: { menu: activeMenu },
    intervalsErrors: { [ListItemType.Standard]: intervalsErrors },
    listItems: { [ListItemType.Standard]: listItems },
    setIntervalsErrors,
    setModifiedListItems,
  } = useScheduleContext();

  const handleValidateInterval = useCallback(
    (dayIndex: number): IntervalsSelectProps['validate'] =>
      (intervals) =>
        void setIntervalsErrors(
          ListItemType.Standard,
          validateIntervals({
            intervals,
            dayIndex,
            errors: intervalsErrors,
          }),
        ),
    [intervalsErrors, setIntervalsErrors],
  );

  const handleIntervalsChange = useCallback(
    (id: string) => (intervals: OpeningHours['intervals']) => {
      const weekDay = listItems.find(({ id: weekDayId }) => weekDayId === id);

      if (!weekDay) {
        throw new Error('Weekday is not provided!');
      }

      setModifiedListItems(ListItemType.Standard, {
        [id]: {
          ...weekDay,
          intervals,
        },
      });
    },
    [listItems, setModifiedListItems],
  );

  const context = useMemo(
    () => ({
      intervalsSelectProps: {
        errors: intervalsErrors,
        onChange: handleIntervalsChange,
        validate: handleValidateInterval,
      },
      onAudioMessageChange: (_) => onAudioMessageChange,
      onAutoReplyChange: (_) => onAutoReplyChange,
      onAutoReplyClear: (_) => onAutoReplyChange,
      onDelete: (_) => noop,
      onOverriddenChange: noop,
    }),
    [
      intervalsErrors,
      handleIntervalsChange,
      handleValidateInterval,
      onAudioMessageChange,
      onAutoReplyChange,
    ],
  );

  return (
    <>
      <ListHeader headers={headers} />

      <ListItemContext.Provider value={context}>
        <Column.Row scrollable noVerticalPadding>
          <FlexBox flexDirection="column" gap="250" padding="0 0 300">
            {listItems.map((listItem, index) => {
              // If active view is not hours, we display only for one
              // item from the list.
              if (activeMenu !== 'hours' && index !== 0) return;

              return (
                <ListItem
                  key={listItem.id}
                  index={index}
                  item={{
                    ...listItem,
                    announcement: audioMessage,
                    auto_reply_template_id: autoReply,
                  }}
                />
              );
            })}
          </FlexBox>
        </Column.Row>
      </ListItemContext.Provider>
    </>
  );
};
